import TomSelect from 'tom-select';

type SelectElementWithTomSelect = HTMLSelectElement & {
  tomselect?: TomSelect;
};

export const getRevealedAssignedSlotElement = (
  dialog: HTMLElement | undefined,
  querySelector: string,
) =>
  (
    dialog?.querySelector(querySelector) as HTMLSlotElement
  )?.assignedElements()?.[0] ?? null;

export const findFirstFocusableRecursive = async (
  container: Element,
): Promise<HTMLElement | TomSelect | null> => {
  const selector = [
    'select',
    'fg-select',
    'button:not([disabled], .sr-only)',
    'button:not(.sr-only)',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  for (const element of container.querySelectorAll(selector)) {
    if (isElementVisible(element as HTMLElement)) {
      return element as HTMLElement;
    }
  }
  return searchInAllShadowRoots(container, selector);
};

export const searchInAllShadowRoots = (
  container: ShadowRoot | Element,
  selector: string,
): HTMLElement | TomSelect | null => {
  const allElements = container.querySelectorAll('*');
  for (const element of allElements) {
    if (element.shadowRoot) {
      const shadowFocusableElements =
        element.shadowRoot.querySelectorAll(selector);

      for (const shadowElement of shadowFocusableElements) {
        if (shadowElement.tagName === 'FG-SELECT') {
          const focusableInFgSelect = (
            shadowElement.shadowRoot?.querySelector(
              'select',
            ) as SelectElementWithTomSelect
          )?.tomselect;
          if (focusableInFgSelect) {
            return focusableInFgSelect;
          }
        }
        if (isElementVisible(shadowElement as HTMLElement)) {
          return shadowElement as HTMLElement;
        }
      }

      const nestedFocusable = searchInAllShadowRoots(
        element.shadowRoot as ShadowRoot,
        selector,
      );
      if (nestedFocusable) {
        return nestedFocusable;
      }
    }
  }

  return null;
};

export const isElementVisible = (element: HTMLElement) => {
  if (!element) return false;

  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  if ('disabled' in element && (element as any).disabled) {
    return false;
  }

  return element.tabIndex >= 0;
};
